import { ArrowLeft, ArrowRight } from "lucide-react";
import * as React from "react";

import {
  AlertaErro,
  AlertaOk,
  Campo,
  LayoutAcesso,
  mensagemDeErro,
} from "@/components/acesso/LayoutAcesso";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Entrada. Não há cadastro público: contas nascem de convite do administrador.
 * "Esqueci minha senha" troca o formulário no lugar, sem sair da tela.
 */
export function LoginPage() {
  const [modo, setModo] = React.useState<"entrar" | "esqueci">("entrar");
  const [email, setEmail] = React.useState("");

  return modo === "entrar" ? (
    <FormEntrar email={email} setEmail={setEmail} aoEsquecer={() => setModo("esqueci")} />
  ) : (
    <FormEsqueci email={email} setEmail={setEmail} aoVoltar={() => setModo("entrar")} />
  );
}

interface PropsEmail {
  email: string;
  setEmail: (valor: string) => void;
}

function FormEntrar({ email, setEmail, aoEsquecer }: PropsEmail & { aoEsquecer: () => void }) {
  const { entrar } = useAuth();
  const [senha, setSenha] = React.useState("");
  const [erro, setErro] = React.useState("");
  const [enviando, setEnviando] = React.useState(false);

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro("");
    setEnviando(true);
    try {
      await entrar(email, senha);
    } catch (falha) {
      setErro(mensagemDeErro(falha, "Não foi possível entrar."));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <LayoutAcesso titulo="Entrar" subtitulo="Entre com sua conta para acessar os decks.">
      <form className="mt-8 grid gap-5" onSubmit={submeter}>
        <Campo
          id="email"
          rotulo="E-mail"
          type="email"
          autoComplete="username"
          placeholder="nome@fs.agr.br"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <Campo
            id="senha"
            rotulo="Senha"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            required
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
          <button
            type="button"
            onClick={aoEsquecer}
            className="mt-2 text-xs font-bold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Esqueci minha senha
          </button>
        </div>

        {erro && <AlertaErro>{erro}</AlertaErro>}

        <Button type="submit" size="lg" disabled={enviando} className="group">
          {enviando ? "Entrando…" : "Entrar"}
          {!enviando && (
            <ArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
          )}
        </Button>
      </form>
    </LayoutAcesso>
  );
}

function FormEsqueci({ email, setEmail, aoVoltar }: PropsEmail & { aoVoltar: () => void }) {
  const [erro, setErro] = React.useState("");
  const [enviado, setEnviado] = React.useState(false);
  const [enviando, setEnviando] = React.useState(false);

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro("");
    setEnviando(true);
    try {
      await api("/auth/senha/esqueci", { method: "POST", body: { email } });
      setEnviado(true);
    } catch (falha) {
      setErro(mensagemDeErro(falha, "Não foi possível enviar o pedido."));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <LayoutAcesso
      titulo="Esqueci minha senha"
      subtitulo="Informe o e-mail da sua conta e enviaremos um link para criar uma nova senha."
    >
      <form className="mt-8 grid gap-5" onSubmit={submeter}>
        <Campo
          id="email"
          rotulo="E-mail"
          type="email"
          autoComplete="username"
          placeholder="nome@fs.agr.br"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEnviado(false);
          }}
        />

        {erro && <AlertaErro>{erro}</AlertaErro>}
        {enviado && (
          <AlertaOk>
            Se o e-mail estiver cadastrado, o link chega em instantes. Ele vale por 1 hora; confira
            também a caixa de spam.
          </AlertaOk>
        )}

        <Button type="submit" size="lg" disabled={enviando}>
          {enviando ? "Enviando…" : enviado ? "Enviar de novo" : "Enviar link"}
        </Button>
        <Button type="button" variant="ghost" onClick={aoVoltar}>
          <ArrowLeft />
          Voltar para entrar
        </Button>
      </form>
    </LayoutAcesso>
  );
}
